import { useCallback } from 'react'
import { socket } from 'utils/io/socket'
import { isSameDay } from 'utils/funnelTracking'
import { getDeviceAndOSInfo } from 'utils/deviceDetection'

// Define action type constants to avoid typos
export const FUNNEL_ACTIONS = {
  CLICKED_PREVIEW: 'clicked_preview',
  ADDED_SCREEN: 'added_screen',
  ADDED_COMPONENT: 'added_component',
  CREATED_COLLECTION: 'created_collection',
  CLICKED_ADD_INTEGRATION: 'clicked_add_integration',
  ADDED_INTEGRATION: 'added_integration',
  PUBLISHED_APP: 'published_app',
  CLICKED_UPGRADE_NOW: 'clicked_upgrade_now',
  UNPUBLISHED_APP: 'unpublished_app',
  CLICKED_SHARE: 'clicked_share',
  CLICKED_SHEETBRIDGE: 'clicked_sheetbridge',
  SHEETBRIDGE_GOT_BRIDGES: 'sheetbridge_got_bridges',
  SHEETBRIDGE_ADDED_COLLECTIONS: 'sheetbridge_added_collections',
  VIEWED_BILLING_TAB: 'viewed_billing_tab',
  CLICKED_UPGRADE_STARTER: 'clicked_upgrade_starter',
  CLICKED_UPGRADE_PRO: 'clicked_upgrade_pro',
  CLICKED_UPGRADE_TEAM: 'clicked_upgrade_team',
  CLICKED_UPGRADE_BUSINESS: 'clicked_upgrade_business',
} as const

export type FunnelActionType =
  typeof FUNNEL_ACTIONS[keyof typeof FUNNEL_ACTIONS]

interface TrackFunnelEventParams {
  actionType: FunnelActionType
  organizationId: number
  location?: string
}

interface TrackFunnelEventResponse {
  success: boolean
  error?: string
  skipped?: boolean
}

interface UseFunnelTracking {
  trackFunnelEvent: (
    params: TrackFunnelEventParams
  ) => Promise<TrackFunnelEventResponse>
  trackClickedPreview: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackAddedScreen: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackAddedComponent: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackCreatedCollection: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackClickedAddIntegration: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackAddedIntegration: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackPublishedApp: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackClickedUpgradeNow: (
    organizationId: number,
    location: string
  ) => Promise<TrackFunnelEventResponse>
  trackUnpublishedApp: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackClickedShare: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackClickedSheetBridge: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackSheetBridgeGotBridges: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackSheetBridgeAddedCollections: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackViewedBillingTab: (
    organizationId: number
  ) => Promise<TrackFunnelEventResponse>
  trackClickedUpgrade: (
    organizationId: number,
    planType: 'starter' | 'pro' | 'team' | 'business'
  ) => Promise<TrackFunnelEventResponse>
  FUNNEL_ACTIONS: typeof FUNNEL_ACTIONS
}

export const useFunnelTracking = (): UseFunnelTracking => {
  const trackFunnelEvent = useCallback(
    async ({
      actionType,
      organizationId,
      location,
    }: TrackFunnelEventParams): Promise<TrackFunnelEventResponse> => {
      if (!socket.connected) {
        console.error('Socket not connected for funnel tracking')

        return { success: false, error: 'Socket not connected' }
      }

      return new Promise(resolve => {
        const timeoutId = setTimeout(() => {
          resolve({ success: false, error: 'Request timeout' })
        }, 5000)

        const deviceInfo = getDeviceAndOSInfo()

        socket.emit(
          'trackFunnelEvent',
          {
            actionType,
            organizationId,
            location,
            deviceInfo,
          },
          (response: TrackFunnelEventResponse) => {
            clearTimeout(timeoutId)
            resolve(response)
          }
        )
      })
    },
    []
  )

  const trackClickedPreview = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_preview_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CLICKED_PREVIEW,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackAddedScreen = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_screen_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.ADDED_SCREEN,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackAddedComponent = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_component_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.ADDED_COMPONENT,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackCreatedCollection = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_collection_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CREATED_COLLECTION,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackClickedAddIntegration = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CLICKED_ADD_INTEGRATION,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackAddedIntegration = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_integration_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.ADDED_INTEGRATION,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackPublishedApp = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_published_app_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.PUBLISHED_APP,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackClickedUpgradeNow = useCallback(
    async (
      organizationId: number,
      location: string
    ): Promise<TrackFunnelEventResponse> => {
      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CLICKED_UPGRADE_NOW,
        organizationId,
        location,
      })
    },
    [trackFunnelEvent]
  )

  const trackClickedShare = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_share_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CLICKED_SHARE,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackClickedSheetBridge = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_sheetbridge_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.CLICKED_SHEETBRIDGE,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackSheetBridgeGotBridges = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.SHEETBRIDGE_GOT_BRIDGES,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackSheetBridgeAddedCollections = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.SHEETBRIDGE_ADDED_COLLECTIONS,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackUnpublishedApp = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const params: TrackFunnelEventParams = {
        actionType: FUNNEL_ACTIONS.UNPUBLISHED_APP,
        organizationId,
      }

      return trackFunnelEvent(params)
    },
    [trackFunnelEvent]
  )

  const trackViewedBillingTab = useCallback(
    async (organizationId: number): Promise<TrackFunnelEventResponse> => {
      const key = `adalo_tracked_billing_tab_${organizationId}`
      const stored = localStorage.getItem(key)

      if (stored && isSameDay(stored, new Date())) {
        return { success: true, skipped: true }
      }

      localStorage.setItem(key, new Date().toISOString())

      return trackFunnelEvent({
        actionType: FUNNEL_ACTIONS.VIEWED_BILLING_TAB,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  const trackClickedUpgrade = useCallback(
    async (
      organizationId: number,
      planType: 'starter' | 'pro' | 'team' | 'business'
    ): Promise<TrackFunnelEventResponse> => {
      let actionType: FunnelActionType
      switch (planType) {
        case 'starter':
          actionType = FUNNEL_ACTIONS.CLICKED_UPGRADE_STARTER

          break
        case 'pro':
          actionType = FUNNEL_ACTIONS.CLICKED_UPGRADE_PRO

          break
        case 'team':
          actionType = FUNNEL_ACTIONS.CLICKED_UPGRADE_TEAM

          break
        case 'business':
          actionType = FUNNEL_ACTIONS.CLICKED_UPGRADE_BUSINESS

          break
        default:
          throw new Error(`Invalid plan type: ${planType as string}`)
      }

      return trackFunnelEvent({
        actionType,
        organizationId,
      })
    },
    [trackFunnelEvent]
  )

  return {
    trackFunnelEvent,
    trackClickedPreview,
    trackAddedScreen,
    trackAddedComponent,
    trackCreatedCollection,
    trackClickedAddIntegration,
    trackAddedIntegration,
    trackPublishedApp,
    trackClickedUpgradeNow,
    trackUnpublishedApp,
    trackClickedShare,
    trackClickedSheetBridge,
    trackSheetBridgeGotBridges,
    trackSheetBridgeAddedCollections,
    trackViewedBillingTab,
    trackClickedUpgrade,
    FUNNEL_ACTIONS,
  }
}
